import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { baseApi } from "./baseApi";
import axios from "axios";

export const useGetUserWalkthroughStatus = (user_id?: string) => {
  return useQuery({
    queryKey: ["user-walkthrough-status", user_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_user_walkthrough_status?user_id=${user_id}`,
      );
      return response;
    },
    enabled: !!user_id,
    retry: 5,
  });
};

export const usePostSetUserWalkthroughStatus = () => {
  return useMutation({
    mutationKey: ["user-walkthrough-status"],
    mutationFn: async ({
      user_id,
      step,
    }: {
      user_id?: string;
      step?: string;
    }) => {
      const response = await baseApi.post(
        `/set_user_walkthrough_status?user_id=${user_id}&walkthrough_stage=${step}`,
      );
      return response;
    },
  });
};

export const usePostSupportRequest = () => {
  return useMutation({
    mutationKey: ["support-request"],
    mutationFn: async ({
      user_id,
      messege,
      source,
    }: {
      user_id?: string;
      messege?: string;
      source?: string;
    }) => {
      const response = await baseApi.post(
        `/support_request?user_id=${user_id}&message=${messege}&source=${source}`,
      );
      return response;
    },
  });
};

export const useGetUserNotification = (user_id?: string, page?: number) => {
  return useQuery({
    queryKey: ["get_deployment_notifications", user_id, page],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_deployment_notifications?user_id=${user_id}&page=${
              page || 1
            }&size=5`, //default limit for notification
          );
          return response;
        },
    enabled: !!user_id,
    retry: 0,
    staleTime: 3,
  });
};

export const useGetUserAllNotification = (user_id?: string, page?: number) => {
  return useQuery({
    queryKey: ["get_all_notification_deployments", user_id, page],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_all_notification_deployments?user_id=${user_id}&page=${
              page || 1
            }&size=5`, //default limit for notification
          );
          return response;
        },
    enabled: !!user_id,
    retry: 0,
    staleTime: 3,
  });
};

export const usePostReadDeploymentNotification = () => {
  return useMutation({
    mutationKey: ["read_deployment_notifications"],
    mutationFn: async ({ user_id }: { user_id?: string }) => {
      const response = await baseApi.post(
        `/read_deployment_notifications?user_id=${user_id}`,
      );
      return response;
    },
  });
};

export const usePostSetLanguagePref = () => {
  return useMutation({
    mutationKey: ["set_language_preference"],
    mutationFn: async ({
      user_id,
      language_pref,
    }: {
      user_id?: string;
      language_pref?: string;
    }) => {
      const response = await baseApi.post(
        `/set_language_preference?user_id=${user_id}&language_pref=${language_pref}`,
      );
      return response;
    },
  });
};
export const usePostLogin = () => {
  return useMutation({
    mutationKey: ["login"],
    mutationFn: async ({ formData }: { formData: any }) => {
      const response = await baseApi.post(`/login`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      return response;
    },
  });
};

export const usePostGoogleAuth = () => {
  return useMutation({
    mutationKey: ["auth_google"],
    mutationFn: async ({ idToken }: { idToken: string }) => {
      const response = await baseApi.post(`/auth/google`, { idToken });
      return response;
    },
  });
};

export const usePostReLogin = () => {
  return useMutation({
    mutationKey: ["re-login"],
    mutationFn: async () => {
      const response = await baseApi.post(`/session/relogin`);

      return response;
    },
  });
};

export const usePostVerify = () => {
  return useMutation({
    mutationKey: ["verify_session"],
    mutationFn: async () => {
      const response = await baseApi.post(`/verify_session`);

      return response;
    },
  });
};

export const usePostUpdateUserDescribtion = () => {
  return useMutation({
    mutationKey: ["update_user_description"],
    mutationFn: async ({
      user_id,
      description,
    }: {
      user_id: string;
      description: string;
    }) => {
      const response = await baseApi.post(
        `/update_user_description?user_id=${user_id}&description=${description}`,
      );

      return response;
    },
  });
};

export const usePostUpdateSocials = () => {
  return useMutation({
    mutationKey: ["update_user_socials"],
    mutationFn: async ({
      user_id,
      twitter,
      linkedin,
    }: {
      user_id: string;
      twitter: string;
      linkedin: string;
    }) => {
      const response = await baseApi.post(
        `/update_user_socials?user_id=${user_id}&twitter=${twitter}&linkedin=${linkedin}`,
      );

      return response;
    },
  });
};

export const usePostUploadProfilePhoto = () => {
  return useMutation({
    mutationKey: ["upload_profile_image"],
    mutationFn: async ({
      user_id,
      formData,
    }: {
      user_id: string;
      formData: any;
    }) => {
      const response = await baseApi.post(
        `/upload_profile_image?user_id=${user_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      return response;
    },
  });
};

export const useGetUserSubscriptionStatus = () => {
  return useMutation({
    mutationKey: ["check_subscription_status"],
    mutationFn: async () => {
      const response = await baseApi.get(`/check_subscription_status`);
      return response;
    },
  });
};

export const useGetUserAccountBalances = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_account_balances", user_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_account_balances?user_id=${user_id}`,
          );
          return response;
        },
    enabled: !!user_id,
    refetchInterval: 20000,
  });
};

export const useGetUserLatencySlipage = (
  user_id?: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["get_broker_user_latency_slippage", user_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_broker_user_latency_slippage?user_id=${user_id}`,
          );
          return response;
        },
    enabled: enabled,
    retry: 0,
  });
};

export const usePostLeverateAuth = () => {
  return useMutation({
    mutationKey: ["login"],
    mutationFn: async (token: string) => {
      const response = await axios.post(
        "https://hub.trylevel2.com/leverate_auth",
        {}, // request body
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    },
  });
};
export const usePostUpgradePlan = () => {
  return useMutation({
    mutationKey: ["check_subscription_status"],
    mutationFn: async ({
      user_id,
      coupon,
      message,
      source,
    }: {
      user_id?: string;
      coupon?: string;
      message?: string;
      source?: string;
    }) => {
      const response = await baseApi.post(
        `/upgrade_plan?user_id=${user_id}&coupon=${coupon}&message=${message}&source=${source}`,
      );
      return response;
    },
  });
};

export const useGetPricingPlanDetails = (user_id?: string, coupon?: string) => {
  return useQuery({
    queryKey: ["get_pricing_plan_details", user_id, coupon],
    queryFn:
      !user_id || !coupon
        ? skipToken
        : async () => {
            const response = await baseApi.post(
              `fetch_pricing_plan_details?user_id=${user_id}&coupon=${coupon}`,
            );
            return response;
          },
    enabled: !!user_id && !!coupon,
    retry: 0,
  });
};
